import { Text, VSpacer } from '@/components/DesignSystem';
import { AppHeader } from '@/components/shared/AppHeader';
import { DesktopOnly } from '@/components/shared/DesktopOnly';
import { MobileOnly } from '@/components/shared/MobileOnly';
import { Routes } from '@/constants/Routes';
import AdminList, { AdminUsersTab } from '@/pages/Admin/AdminList/AdminList';
import { DebugPage, DebugTab, LegacyDebugRedirect } from '@/pages/Admin/Debug/DebugPage';
import FeatureFlags from '@/pages/Admin/FeatureFlags';
import {
  LocationDetailsOverviewPage,
  RetailerDetailsOverviewPage,
  RetailerListPage,
} from '@/pages/Admin/HierarchyOfRetailers';
import RetailerWalletOverviewPage
  from "@/pages/Admin/HierarchyOfRetailers/Wallet/RetailerWalletOverviewPage";
import ManufacturerDetails from '@/pages/Admin/Manufacturers/ManufacturerDetails';
import ManufacturerList from '@/pages/Admin/Manufacturers/ManufacturerList';
import { NotificationBuilder } from '@/pages/Admin/NotificationBuilder/NotificationBuilder';
import { ReportBasePage } from '@/pages/Reports/ReportBasePage';
import { Reports } from '@/pages/Reports/Reports';
import {
  ScheduledNotificationRecipientsPage,
} from '@/pages/Admin/NotificationBuilder/components/ScheduledNotificationRecipientsPage';
import OrdersOverview from '@/pages/Admin/Orders/OrdersOverview';
import { TranslationExplorer } from '@/pages/Admin/TranslationExplorer/TranslationExplorer';
import { PointsMetrics } from '@/pages/Admin/Metrics/PointsMetrics';
import { RetailerOverview } from '@/pages/RetailerOverview/RetailerOverview';
import { PointsAuditProcesses } from '@/pages/Admin/PointsAudit/PointsAuditProcesses';
import { PointsAuditTrail } from '@/pages/Admin/PointsAudit/PointsAuditTrail';
import { UserAccounts } from '@/pages/Admin/UserAccounts/UserAccounts';
import { UserInspector } from '@/pages/Admin/UserInspector/UserInspector';
import { UXTestingPage } from '@/pages/Admin/UXTestingPage/UXTestingPage';
import { Bookings } from '@/pages/Bookings';
import { BenefitsPage } from '@/pages/CustomerDashboard/BenefitsPage';
import { CustomerDashboard } from '@/pages/CustomerDashboard/CustomerDashboard';
import { HowItWorksPage } from '@/pages/CustomerDashboard/HowItWorksPage';
import { PromotionsPage } from '@/pages/CustomerDashboard/Promotions/PromotionsPage';
import { WalletPage } from '@/pages/CustomerDashboard/WalletPage';
import CustomerDetails from '@/pages/FarmerList/CustomerDetails';
import CustomerList from '@/pages/FarmerList/CustomerList';
import ProductList from '@/pages/Product/ProductList';
import { Promotions } from '@/pages/Promotions/Promotions';
import { UIShellDemo } from '@/pages/UXSandbox';
import { UXSandbox } from '@/pages/UXSandbox/UXSandbox';
import { RetailerAppFooter, retailerAppFooterHeight } from '@/views/RetailerAppFooter';
import { Box } from '@mui/material';
import React, { ReactNode } from 'react';
import { Navigate, Route, Routes as AppRoutes } from 'react-router-dom';

const legacyDebugRoutes: [string, Routes][] = [
  ['/admin/metrics', Routes.ADMIN_METRICS],
  ['/admin/points-audit', Routes.ADMIN_POINTS_AUDIT],
  ['/admin/points-audit/processes', Routes.ADMIN_POINTS_AUDIT_PROCESSES],
  ['/admin/user-inspector', Routes.ADMIN_USER_INSPECTOR],
  ['/admin/ux-testing/*', Routes.UX_TESTING],
];

interface LayoutOptions {
  /** The page sizes itself to end at the footer, so it needs no extra space below. */
  fillsWindow?: boolean,
  /** The UX sandbox previews components at mobile widths too. */
  showsOnMobile?: boolean,
}

export const InternalView = () => {
  const createLayout = (element: ReactNode, options: LayoutOptions = {}) => {
    return (
      <>
        <AppHeader />
        <VSpacer size="12" />
        <VSpacer size="3" />
        {options.showsOnMobile ? element : (
          <>
            <DesktopOnly>
              {element}
            </DesktopOnly>
            <MobileOnly>
              <Text>Please switch to desktop to use the admin console</Text>
            </MobileOnly>
          </>
        )}
        {options.fillsWindow ? <Box height={retailerAppFooterHeight} /> : (
          <>
            <VSpacer size="12" />
            <VSpacer size="3" />
            <VSpacer size="11" />
          </>
        )}
        <RetailerAppFooter />
      </>
    );
  };

  const createReportLayout = (element: ReactNode) => {
    return (
      <>
        {element}
        <RetailerAppFooter />
      </>
    );
  };

  const createDebugLayout = (tab: DebugTab, element: ReactNode, options?: LayoutOptions) => (
    createLayout(<DebugPage tab={tab}>{element}</DebugPage>, options)
  );

  const createUXLayout = (element: ReactNode) => {
    return (
      <>
        <AppHeader />
        <VSpacer size="11" />
        {element}
      </>
    );
  };

  return (
    <AppRoutes>
      <Route
        element={createLayout(<AdminList />)}
        path={Routes.ADMIN_LIST_ADMINS}
      />
      <Route
        element={createLayout(<AdminList tab={AdminUsersTab.CognitoSignInSync} />)}
        path={Routes.ADMIN_COGNITO_SIGN_IN_SYNC}
      />
      <Route
        element={createLayout(<Bookings />)}
        path={Routes.ADMIN_BOOKINGS}
      />
      <Route
        element={createLayout(<CustomerList />)}
        path={Routes.CUSTOMERS}
      />
      <Route
        element={createLayout(<FeatureFlags />)}
        path={Routes.FEATURE_FLAGS}
      />
      <Route
        element={createLayout(<ManufacturerList />)}
        path={Routes.ADMIN_LIST_MANUFACTURERS}
      />
      <Route
        element={createLayout(<ManufacturerDetails />)}
        path={`${Routes.ADMIN_LIST_MANUFACTURERS}/:id`}
      />
      <Route
        element={createLayout(<OrdersOverview />)}
        path={Routes.ADMIN_LIST_ORDERS}
      />
      <Route
        element={createLayout(<NotificationBuilder />)}
        path={Routes.ADMIN_NOTIFICATION_BUILDER}
      />
      <Route
        element={createReportLayout(<ScheduledNotificationRecipientsPage />)}
        path={Routes.ADMIN_SCHEDULED_NOTIFICATION}
      />
      <Route
        element={createLayout(<RetailerListPage />)}
        path={Routes.ADMIN_HIERARCHY_OF_RETAILERS}
      />
      <Route
        element={createLayout(
            <RetailerDetailsOverviewPage />,
        )}
        path={`${Routes.ADMIN_HIERARCHY_OF_RETAILERS}/:id`}
      />
      <Route
        element={createLayout(
          <RetailerWalletOverviewPage />,
        )}
        path={Routes.RETAILER_WALLET}
      />
      <Route
        element={createLayout(
            <LocationDetailsOverviewPage />,
        )}
        path={`${Routes.ADMIN_HIERARCHY_OF_RETAILERS}/:id/location/:locationId`}
      />
      <Route element={createUXLayout(<UXSandbox />)} path={Routes.UX_SANDBOX}>
        <Route element={createUXLayout(<UXSandbox />)} path=':componentName' />
      </Route>
      <Route element={<UIShellDemo showDemo />} path={Routes.UI_SHELL_DEMO} />
      <Route element={createLayout(<ProductList />)} path={Routes.PRODUCTS} />
      <Route element={createLayout(<Reports />)} path={Routes.REPORTS} />
      <Route element={createLayout(<ReportBasePage />)} path={Routes.REPORT_PAGE} />
      <Route
        element={createLayout(<Promotions />)}
        path={Routes.PROMOTIONS}
      />
      <Route
        element={<CustomerDashboard />}
        path={Routes.CUSTOMER_DASHBOARD}
      />
      <Route
        element={<BenefitsPage />}
        path={Routes.CUSTOMER_DASHBOARD_BENEFITS}
      />
      <Route
        element={<HowItWorksPage />}
        path={Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS}
      />
      <Route
        element={<WalletPage />}
        path={Routes.CUSTOMER_DASHBOARD_WALLET}
      />
      <Route
        element={<PromotionsPage />}
        path={Routes.CUSTOMER_DASHBOARD_PROMOTIONS}
      />
      <Route element={createLayout(<CustomerDetails />)} path={Routes.CUSTOMER_DETAILS} />
      <Route
        element={createLayout(<TranslationExplorer />)}
        path={Routes.TRANSLATION_EXPLORER}
      />
      <Route
        element={<Navigate replace to={Routes.ADMIN_POINTS_AUDIT} />}
        path={Routes.ADMIN_DEBUG}
      />
      {/* Processes must come first: the trail path is a prefix of it. */}
      <Route
        element={createDebugLayout(DebugTab.PointsAudit, <PointsAuditProcesses />)}
        path={Routes.ADMIN_POINTS_AUDIT_PROCESSES}
      />
      <Route
        element={createDebugLayout(DebugTab.PointsAudit, <PointsAuditTrail />)}
        path={Routes.ADMIN_POINTS_AUDIT}
      />
      <Route
        element={createDebugLayout(DebugTab.Metrics, <PointsMetrics />)}
        path={Routes.ADMIN_METRICS}
      />
      <Route
        element={createDebugLayout(DebugTab.UserInspector, <UserInspector />)}
        path={Routes.ADMIN_USER_INSPECTOR}
      />
      <Route
        element={createDebugLayout(DebugTab.UserAccounts, <UserAccounts />, { fillsWindow: true })}
        path={Routes.ADMIN_USER_ACCOUNTS}
      />
      <Route
        element={(
          createDebugLayout(DebugTab.UxTesting, <UXTestingPage />, { showsOnMobile: true })
        )}
        path={Routes.UX_TESTING}
      />
      <Route
        element={(
          createDebugLayout(DebugTab.UxTesting, <UXTestingPage />, { showsOnMobile: true })
        )}
        path={`${Routes.UX_TESTING}/:componentName`}
      />
      {legacyDebugRoutes.map(([path, to]) => (
        <Route element={<LegacyDebugRedirect to={to} />} key={path} path={path} />
      ))}
      {/* Opened from Reports with ?retailerId=, to see a retailer's landing page as they do. */}
      <Route
        element={createLayout(<RetailerOverview />)}
        path={Routes.RETAILER_OVERVIEW}
      />
      <Route
        element={<Navigate to={Routes.CUSTOMERS} />}
        path={'*'}
      />
    </AppRoutes>
  );
};
